import { sfx } from '../audio/sfx';
import { block, ellipse, rect, shadow, type Ctx } from '../render/paint';
import { px, type MapId } from './mapdef';
import { CAR_LANES, TOWN_SIZE } from './maps/town';

interface Car {
  x: number;
  y: number;
  dir: 1 | -1;
  speed: number;
  color: string;
  honked: boolean;
  braking: boolean;
  /** Scooters are half as long, quicker and far more numerous — this is Taiwan. */
  scooter: boolean;
  /** Rider's jacket / helmet colours (scooters). */
  rider: number;
}

interface Bird {
  hx: number;
  hy: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  flying: boolean;
  away: number;
  peck: number;
}

interface Butterfly {
  x: number;
  y: number;
  t: number;
  color: string;
}

const CAR_COLORS = ['#d0453a', '#3a8fd9', '#f4f1ea', '#ffd34e', '#57c278', '#2b3440', '#9b5fc0'];

/** Ambient life on the town map. Purely cosmetic, apart from cars braking for you. */
export class Ambient {
  cars: Car[] = [];
  birds: Bird[] = [];
  flies: Butterfly[] = [];
  private spawnIn = [1.5, 4];

  private scooterIn = [0.5, 2];

  constructor() {
    // Two flocks: one in the park, one pecking around the school plaza.
    for (const [base, n] of [[{ x: px(34), y: px(34) }, 7], [{ x: px(28), y: px(16.5) }, 5]] as [{ x: number; y: number }, number][]) {
      for (let i = 0; i < n; i++) {
        const hx = base.x + (Math.random() - 0.5) * 40, hy = base.y + (Math.random() - 0.5) * 20;
        this.birds.push({ hx, hy, x: hx, y: hy, z: 0, vx: 0, vy: 0, flying: false, away: 0, peck: Math.random() * 3 });
      }
    }
    const spots = [
      [px(45), px(12)], [px(40), px(17)], [px(25), px(32)], [px(48), px(41)], [px(33), px(43)], [px(8), px(41)],
      [px(31), px(43)], [px(25), px(56)], [px(63), px(53)], [px(7), px(44)], [px(50), px(11)],
    ];
    for (const [x, y] of spots) this.flies.push({ x, y, t: Math.random() * 10, color: ['#ffd34e', '#ffffff', '#ff9fc0', '#9fd6ff'][Math.floor(Math.random() * 4)] });
  }

  update(dt: number, map: MapId, player: { x: number; y: number }, night: number, bus: { x: number; y: number } | null = null): void {
    if (map !== 'town') return;
    // Cars.
    CAR_LANES.forEach((lane, i) => {
      if ((this.spawnIn[i] -= dt) <= 0) {
        this.spawnIn[i] = 3 + Math.random() * 6 + night * 6;
        const dir = lane.dir as 1 | -1;
        this.cars.push({
          x: dir > 0 ? -40 : TOWN_SIZE.w + 40,
          y: lane.y,
          dir,
          speed: 55 + Math.random() * 25,
          color: CAR_COLORS[Math.floor(Math.random() * CAR_COLORS.length)],
          honked: false,
          braking: false,
          scooter: false,
          rider: 0,
        });
      }
      if ((this.scooterIn[i] -= dt) <= 0) {
        this.scooterIn[i] = 2 + Math.random() * 5 + night * 4;
        const dir = lane.dir as 1 | -1;
        this.cars.push({
          x: dir > 0 ? -30 : TOWN_SIZE.w + 30,
          y: lane.y + (Math.random() < 0.5 ? -3 : 3),
          dir,
          speed: 70 + Math.random() * 30,
          color: CAR_COLORS[Math.floor(Math.random() * CAR_COLORS.length)],
          honked: false,
          braking: false,
          scooter: true,
          rider: Math.floor(Math.random() * 7),
        });
      }
    });
    for (let i = this.cars.length - 1; i >= 0; i--) {
      const car = this.cars[i];
      const len = car.scooter ? 9 : 18;
      const front = car.x + car.dir * len;
      const ahead = (player.x - front) * car.dir;
      const inLane = Math.abs(player.y - (car.y + 2)) < 15;
      // Brake for the player, and for the vehicle in front (scooters weave past cars in the gaps).
      const blockedByCar = this.cars.some((o) => o !== car && Math.abs(o.y - car.y) < (car.scooter ? 2 : 7) && (o.x - car.x) * car.dir > 0 && (o.x - car.x) * car.dir < len + 26);
      // ...and for the bus (its rear end), which also blocks its lane.
      const busAhead = bus && Math.abs(bus.y - (car.y + 2)) < 6 ? (bus.x - front) * car.dir : -1;
      car.braking = (inLane && ahead > -6 && ahead < 46) || blockedByCar || (busAhead > -6 && busAhead < 30);
      if (car.braking && inLane && !car.honked && ahead < 46) {
        car.honked = true;
        if (!car.scooter || Math.random() < 0.4) sfx('honk');
      }
      if (!car.braking) car.x += car.dir * car.speed * dt;
      if (car.x < -80 || car.x > TOWN_SIZE.w + 80) this.cars.splice(i, 1);
    }
    // Pigeons scatter when you get close, then come back.
    for (const b of this.birds) {
      const d = Math.hypot(player.x - b.x, player.y - b.y);
      if (!b.flying && d < 28) {
        b.flying = true;
        const a = Math.atan2(b.y - player.y, b.x - player.x) + (Math.random() - 0.5);
        b.vx = Math.cos(a) * 70;
        b.vy = Math.sin(a) * 40;
        b.away = 0;
        if (Math.random() < 0.3) sfx('flap');
      }
      if (b.flying) {
        b.away += dt;
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        b.z = Math.min(60, b.z + 50 * dt);
        if (b.away > 6 && Math.hypot(player.x - b.hx, player.y - b.hy) > 70) {
          b.flying = false;
          b.x = b.hx;
          b.y = b.hy;
          b.z = 0;
        }
      } else {
        b.peck += dt;
        if (Math.random() < dt * 0.3) b.x += (Math.random() - 0.5) * 6;
      }
    }
    for (const f of this.flies) {
      f.t += dt;
      f.x += Math.cos(f.t * 0.7) * 10 * dt + Math.sin(f.t * 2.3) * 6 * dt;
      f.y += Math.sin(f.t * 0.9) * 6 * dt;
    }
  }

  /** Depth-sorted drawables for the renderer. */
  drawables(map: MapId, t: number, night: number): { y: number; draw: (c: Ctx) => void }[] {
    if (map !== 'town') return [];
    const out: { y: number; draw: (c: Ctx) => void }[] = [];
    for (const car of this.cars) out.push({ y: car.y + 4, draw: (c) => (car.scooter ? drawScooter(c, car, t, night) : drawCar(c, car, night)) });
    for (const b of this.birds) out.push({ y: b.flying ? b.y + 200 : b.y, draw: (c) => drawBird(c, b, t) });
    if (night < 0.5) for (const f of this.flies) out.push({ y: f.y + 40, draw: (c) => drawFly(c, f) });
    return out;
  }

  /** Headlights for the lighting pass. */
  lights(map: MapId): { x: number; y: number; r: number; color: string }[] {
    if (map !== 'town') return [];
    return this.cars.map((c) => (c.scooter ? { x: c.x + c.dir * 14, y: c.y - 4, r: 22, color: '255,240,190' } : { x: c.x + c.dir * 26, y: c.y - 4, r: 34, color: '255,240,190' }));
  }
}

function drawCar(c: Ctx, car: Car, night: number): void {
  const x = Math.round(car.x), y = Math.round(car.y);
  const d = car.dir;
  shadow(c, x, y + 3, 19, 4, 0.35);
  // Wheels.
  for (const wx of [-11, 9]) block(c, x + wx, y - 2, 6, 5, '#1b1d24');
  block(c, x - 18, y - 13, 36, 13, car.color, undefined, undefined);
  rect(c, 'rgba(255,255,255,0.3)', x - 17, y - 12, 34, 2);
  rect(c, 'rgba(0,0,0,0.2)', x - 17, y - 3, 34, 2);
  // Cabin with windows.
  block(c, x - 10 - d * 2, y - 22, 20, 10, car.color);
  rect(c, '#9fd3f0', x - 8 - d * 2, y - 20, 7, 6);
  rect(c, '#9fd3f0', x + 1 - d * 2, y - 20, 7, 6);
  rect(c, 'rgba(255,255,255,0.6)', x - 7 - d * 2, y - 19, 2, 4);
  // Lights.
  rect(c, '#fff6c0', x + d * 16 - (d > 0 ? 1 : 0), y - 10, 2, 3);
  rect(c, car.braking ? '#ff3a2a' : '#a8261c', x - d * 16 - (d > 0 ? 1 : 0), y - 10, 2, 3);
  if (night > 0.3) ellipse(c, `rgba(255,240,180,${0.25 * night})`, x + d * 30, y - 6, 14, 5);
}

const JACKETS = ['#3a8fd9', '#e2493b', '#57c278', '#2a2a30', '#f2c94c', '#8a5aa8', '#f4f1ea'];
/** A scooter and its rider, seen from the side, with a little bounce and a raincoat-coloured jacket. */
function drawScooter(c: Ctx, car: Car, t: number, night: number): void {
  const x = Math.round(car.x), y = Math.round(car.y);
  const d = car.dir;
  const bob = car.braking ? 0 : Math.round(Math.sin(t * 22 + x) * 0.6);
  shadow(c, x, y + 3, 10, 2.6, 0.3);
  ellipse(c, '#1b1d24', x - d * 7, y - 1, 2.8, 2.8);
  ellipse(c, '#1b1d24', x + d * 7, y - 1, 2.8, 2.8);
  ellipse(c, '#6a6e74', x - d * 7, y - 1, 1, 1);
  ellipse(c, '#6a6e74', x + d * 7, y - 1, 1, 1);
  block(c, x - 8, y - 8 + bob, 15, 6, car.color);
  block(c, x + d * 4.5 - 2, y - 13 + bob, 4, 11, car.color);
  rect(c, '#2a1810', x + d * 4 - 3.5, y - 14 + bob, 7, 1.3);
  rect(c, '#fff6c0', x + d * 7.5 - 1, y - 10.5 + bob, 2, 2);
  rect(c, car.braking ? '#ff3a2a' : '#a8261c', x - d * 8.5 - 0.5, y - 7 + bob, 1.4, 1.6);
  // Rider: legs tucked, jacket, helmet with a visor.
  const jacket = JACKETS[car.rider % JACKETS.length];
  block(c, x - d * 2 - 3, y - 11 + bob, 6, 4, '#2f3442');
  block(c, x - d * 2 - 3.5, y - 19 + bob, 7, 8, jacket);
  rect(c, '#f6cfa8', x + d * 1 - 1, y - 16 + bob, 3, 1.4);
  ellipse(c, '#2a1810', x - d * 1.5, y - 22 + bob, 3.8, 3.6);
  ellipse(c, ['#ffffff', '#e2493b', '#3a8fd9', '#1b1d24', '#ffd34e'][car.rider % 5], x - d * 1.5, y - 22 + bob, 3.2, 3);
  rect(c, 'rgba(40,30,20,0.75)', x - d * 1.5 + (d > 0 ? 0 : -3), y - 22.5 + bob, 3, 1.6);
  if (car.rider % 3 === 0) block(c, x - d * 9 - 3, y - 15 + bob, 6, 5, '#ff2d8a', '#ff6ab0', '#b0105a');
  if (night > 0.3) ellipse(c, `rgba(255,240,180,${0.22 * night})`, x + d * 18, y - 5, 9, 3.5);
}

function drawBird(c: Ctx, b: Bird, t: number): void {
  const x = Math.round(b.x), y = Math.round(b.y - b.z);
  if (!b.flying) shadow(c, x, b.y, 3, 1.2);
  const peck = !b.flying && Math.sin(b.peck * 4) > 0.7 ? 1 : 0;
  ellipse(c, '#2a1810', x, y - 3, 4, 3);
  ellipse(c, '#8a8f9c', x, y - 3, 3.2, 2.2);
  rect(c, '#6a6f7c', x + 1, y - 7 + peck, 3, 3);
  rect(c, '#e8a020', x + 4, y - 6 + peck, 1, 1);
  if (b.flying) {
    const flap = Math.sin(t * 30) > 0 ? -3 : 1;
    rect(c, '#9aa0ac', x - 5, y - 4 + flap, 4, 1);
    rect(c, '#9aa0ac', x + 1, y - 4 + flap, 4, 1);
  }
}

function drawFly(c: Ctx, f: Butterfly): void {
  const open = Math.sin(f.t * 18) > 0;
  const x = Math.round(f.x), y = Math.round(f.y - 14 - Math.sin(f.t * 2) * 3);
  c.fillStyle = f.color;
  if (open) {
    c.fillRect(x - 3, y - 1, 2, 2);
    c.fillRect(x + 1, y - 1, 2, 2);
  } else c.fillRect(x - 1, y - 2, 2, 2);
  c.fillStyle = '#2a1810';
  c.fillRect(x, y - 1, 1, 2);
}

/** The city bus (line 8): long, green and cream, with big windows. */
export function drawBus(c: Ctx, bx: number, by: number, d: 1 | -1, night: number, doorOpen: boolean, t: number): void {
  const x = Math.round(bx), y = Math.round(by);
  const L = 33;
  shadow(c, x, y + 3, L + 2, 5, 0.38);
  for (const wx of [-L + 8, L - 16]) block(c, x + wx, y - 3, 8, 6, '#1b1d24');
  // Body: cream top, green skirt.
  block(c, x - L, y - 30, L * 2, 30, '#f2ead6');
  rect(c, '#2e9d6a', x - L + 1, y - 10, L * 2 - 2, 9);
  rect(c, '#ffd34e', x - L + 1, y - 11, L * 2 - 2, 1);
  rect(c, 'rgba(255,255,255,0.45)', x - L + 2, y - 29, L * 2 - 4, 2);
  // Windows (a passenger silhouette in a few).
  for (let i = 0; i < 6; i++) {
    const wx = x - L + 5 + i * 10;
    rect(c, '#7fbfe0', wx, y - 25, 8, 9);
    rect(c, 'rgba(255,255,255,0.5)', wx + 1, y - 24, 2, 7);
    if ((i * 7 + 3) % 4 === 0) {
      ellipse(c, '#3a2a20', wx + 5, y - 19, 2.2, 2.2);
      rect(c, '#5a6f8a', wx + 3, y - 17, 4, 2);
    }
  }
  // Front door, near the nose: slides open at stops.
  const door = x + d * (L - 12) - 4;
  rect(c, doorOpen ? '#2a2420' : '#5c8fa8', door, y - 25, 8, 22);
  if (!doorOpen) rect(c, '#e8eef2', door + 3.5, y - 25, 1, 22);
  // Route sign and lights.
  block(c, x + d * (L - 6) - 5, y - 34, 10, 5, '#2a1810');
  rect(c, '#ffb43a', x + d * (L - 6) - 3, y - 33, 6, 3);
  rect(c, '#fff6c0', x + d * L - (d > 0 ? 2 : 0), y - 8, 2, 3);
  rect(c, '#c0392b', x - d * L - (d > 0 ? 0 : 2), y - 8, 2, 3);
  if (night > 0.3) ellipse(c, `rgba(255,240,180,${0.25 * night})`, x + d * (L + 14), y - 4, 14, 5);
  // A little engine wobble.
  if (!doorOpen) rect(c, 'rgba(120,120,120,0.35)', x - d * (L + 3) + Math.sin(t * 20), y - 3, 3, 2);
}
